import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import '../components/Contact.css';

const GetInvolvedPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS credentials from environment variables
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_d6jjww9';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_jsl3ujt';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'nLsyduh05SkoaGYzB';

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: `Get Involved - ${formData.interest}`,
        message: `New Get Involved submission:\n\nName: ${formData.name}\nEmail: ${formData.email}\nArea of Interest: ${formData.interest}\n\nMessage:\n${formData.message || 'No message provided'}`,
        to_name: 'E-Q.O.L. Team',
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const involvementOptions = [
    {
      title: 'Join a Team',
      description: 'Become part of Engineering & Innovation, Volunteering & Community Engagement, or Research & Awareness teams.',
      icon: '👥'
    },
    {
      title: 'Volunteer',
      description: 'Help organize events, connect with seniors, and make a direct impact in your community.',
      icon: '🤝'
    },
    {
      title: 'Partner With Us',
      description: 'Are you an organization or institution? Let\'s collaborate to create meaningful change together.',
      icon: '🏢'
    },
    {
      title: 'Spread the Word',
      description: 'Share our mission, follow our newsletter, and help us reach more people who want to make a difference.',
      icon: '📢'
    }
  ];

  return (
    <div className="page-container">
      <section className="contact section" ref={sectionRef}>
        <div className="container">
          <div className={`contact-header ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="section-title">Get Involved</h2>
            <p className="section-subtitle">
              Ready to make a difference? There are many ways to get involved with E-Q.O.L. and help bridge generations.
            </p>
          </div>

          <div className="involvement-options">
            {involvementOptions.map((option, index) => (
              <div 
                key={index} 
                className={`involvement-card ${isVisible ? 'animate-slide-up' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="involvement-icon">{option.icon}</div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </div>
            ))}
          </div>

          <div className={`contact-form-wrapper ${isVisible ? 'animate-fade-in' : ''}`}>
            <div className="contact-form-container">
              <h3>Tell Us About Yourself</h3>
              <p>Fill out the form below and we'll get in touch with you about how you can get involved.</p>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interest">Area of Interest *</label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select an option</option>
                    <option value="engineering">Engineering & Innovation</option>
                    <option value="volunteering">Volunteering & Community Engagement</option>
                    <option value="research">Research & Awareness</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Tell us why you're interested in getting involved..."
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="success-message">
                    <p>✅ Thank you for your interest! We'll get back to you soon.</p>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    <p>❌ Sorry, there was an error sending your message. Please try again or contact us directly.</p>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>

          <div className="contact-cta">
            <p>Have questions? <Link to="/contact">Contact us directly</Link> or check out our <Link to="/teams">teams page</Link> to learn more.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolvedPage;

